import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Button from 'Components/DesignSystem/Button/Button';

// API Services
import { searchProducts, SearchProductResult } from 'api/DataReview';

// Types
import { ProductAttribute } from '../types';
import { getValues } from '../utils';

// Close icon component
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M5 5l10 10M15 5L5 15"
    />
  </svg>
);

// Search icon for search input
const SearchIconSmall = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
    <circle cx="7" cy="7" r="5" strokeWidth="1.5" />
    <path strokeLinecap="round" strokeWidth="1.5" d="M11 11l3 3" />
  </svg>
);

// Chevron icons for expandable rows
const ChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 8l5 5 5-5" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 12l5-5 5 5" />
  </svg>
);

// Props interface
interface ImportedProductData {
  name: string;
  category: string;
  sku?: string;
  price?: string;
  upc?: string;
  modelNumber?: string;
  description?: string;
  pls?: string;
  parentId?: string;
  version?: string;
  realSku?: string;
  attributes: ProductAttribute[];
}

type PanelMode = 'replace' | 'add';

interface SearchReplaceSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  channelKey: string;
  sku: string;
  importedProduct: ImportedProductData;
  onProductSelect?: (productId: string) => void;
  onReplaceProduct?: (productId: string) => void;
  mode?: PanelMode;
  /** True while the parent's replace/add API call is in flight. Disables
   * the action button and swaps its label to a loading state, so the
   * user gets feedback inside the panel rather than a page-level
   * overlay flashing across the view. */
  isReplacing?: boolean;
}

/**
 * SearchReplaceSidePanel Component
 *
 * Side panel for searching and replacing products in MPN/UPC Matched tab.
 * Opens from the right side with a backdrop overlay.
 *
 * Features:
 * - Two-column layout: Imported Product details (left) and Search (right)
 * - Backdrop click to close
 * - Product attributes table
 * - Search functionality with real API integration
 *
 * Figma: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=11668-9128
 */
const SearchReplaceSidePanel = ({
  isOpen,
  onClose,
  channelKey,
  sku,
  importedProduct,
  onProductSelect,
  onReplaceProduct,
  mode = 'replace',
  isReplacing = false,
}: SearchReplaceSidePanelProps) => {
  const isAddMode = mode === 'add';
  const panelTitle = isAddMode ? 'Search & Add Product' : 'Search & Replace Products';
  const sectionTitle = isAddMode ? 'Search & Add' : 'Search & Replace';
  const idleActionLabel = isAddMode ? 'Add Product' : 'Replace Product';
  const inFlightActionLabel = isAddMode ? 'Adding…' : 'Replacing…';
  const actionButtonLabel = isReplacing ? inFlightActionLabel : idleActionLabel;
  const emptyStateText = isAddMode
    ? 'Enter a search term to find products to add'
    : 'Enter a search term to find products to replace';
  const [searchValue, setSearchValue] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchProductResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search effect
  useEffect(() => {
    if (!searchValue || searchValue.length < 2 || !channelKey) {
      setSearchResults([]);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await searchProducts(channelKey, {
          q: searchValue,
          limit: 20,
        });
        if (response.done) {
          setSearchResults(response.data || []);
        }
      } catch (error) {
        console.error('Search failed:', error);
        toast.error('Failed to search products');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchValue, channelKey]);

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
      setSelectedProductId(null);
      setExpandedRowId(null);
      setSearchResults([]);
    }
  }, [isOpen]);

  // Toggle row expansion - per Figma 11915-17125
  const handleRowExpand = (id: string) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };

  if (!isOpen) {
    return null;
  }

  // Render metadata row (label + value pair) - per Figma 11668-9816
  // `tw-min-w-0` on the wrapper lets the grid column shrink below the value's
  // natural width (otherwise long Parent IDs / SKUs push the column out and
  // overlap the neighbour). `tw-truncate` on the value gives ellipsis, with
  // `title` showing the full text on hover.
  // `multiline` exempts full-width fields like Description from truncation so
  // the text wraps normally instead of collapsing to a single line.
  const renderMetadataItem = (label: string, value: string, multiline = false) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
      <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">{label}</span>
      <span
        className={`tw-text-sm tw-font-semibold tw-text-ink ${multiline ? 'tw-break-words' : 'tw-truncate'}`}
        title={multiline ? undefined : (value || '-')}
      >
        {value || '-'}
      </span>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div data-testid="search-replace-side-panel-backdrop"
        className="tw-fixed tw-inset-0 tw-bg-scrim tw-transition-opacity tw-duration-300 tw-z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel Container */}
      <div
        className="tw-fixed tw-right-0 tw-top-12 tw-bottom-0 tw-w-[820px] tw-bg-surface tw-shadow-lg tw-z-50 tw-flex tw-flex-col tw-transform tw-transition-transform tw-duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-replace-panel-title"
      >
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-edge">
          <span id="search-replace-panel-title">
            <Typography variant="heading-2" className="tw-text-ink">
              {panelTitle}
            </Typography>
          </span>
          <button data-testid="components-searchreplacesidepanel-button-1"
            onClick={onClose}
            className="tw-p-1 tw-text-ink-secondary hover:tw-text-ink tw-transition-colors"
            aria-label="Close panel"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content - Two Column Layout */}
        <div className="tw-flex tw-flex-1 tw-overflow-hidden">
          {/* Left Column - Imported Product */}
          <div className="tw-w-[50%] tw-border-r tw-border-edge tw-overflow-y-auto tw-p-4 tw-bg-surface-sunken">
            {/* Section Title */}
            <Typography variant="title" className="tw-text-ink-secondary tw-mb-4">
              Imported Product
            </Typography>

            {/* Row 1: Product Name + Category - per Figma 11668-9816 */}
            <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4">
              {renderMetadataItem('Product Name', importedProduct.name)}
              {renderMetadataItem('Category', importedProduct.category)}
            </div>

            {/* Row 2: SKU + Price */}
            <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4">
              {renderMetadataItem('SKU', importedProduct.sku || '')}
              {renderMetadataItem('Price', importedProduct.price || '')}
            </div>

            {/* Row 3: UPC + Model Number */}
            <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4">
              {renderMetadataItem('UPC', importedProduct.upc || '')}
              {renderMetadataItem('Model Number', importedProduct.modelNumber || '')}
            </div>

            {/* Row 4: Description (full width) */}
            <div className="tw-mb-4">
              {renderMetadataItem('Description', importedProduct.description || '', true)}
            </div>

            {/* Row: PLS + Parent ID + Version + Real SKU */}
            <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-mb-4">
              {renderMetadataItem('PLS', importedProduct.pls || '')}
              {renderMetadataItem('Parent ID', importedProduct.parentId || '')}
              {renderMetadataItem('Version', importedProduct.version || '')}
              {renderMetadataItem('Real SKU', importedProduct.realSku || '')}
            </div>

            {/* Attributes - with row backgrounds per Figma 11914-16579 */}
            {importedProduct.attributes.length > 0 && (
              <div className="tw-flex tw-flex-col">
                {/* Header Row */}
                <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-2.5">
                  <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">Attribute</span>
                  <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">Attribute Value</span>
                </div>
                {/* Attribute Rows with striped backgrounds per Figma 11669-8730 */}
                <div className="tw-flex tw-flex-col">
                  {importedProduct.attributes.map((attr, index) => {
                    const vals = getValues(attr);
                    return (
                      <div
                        key={attr.name}
                        className={`tw-grid tw-grid-cols-2 tw-gap-4 tw-py-1.5 tw--mx-4 tw-px-4 ${
                          index % 2 === 0 ? 'tw-bg-surface-sunken' : ''
                        }`}
                      >
                        <span className="tw-text-sm tw-font-semibold tw-text-ink tw-min-w-0 tw-break-words">
                          {attr.name}
                        </span>
                        {/* Render each value as an individual chip (matches
                            the search-result preview on the right). */}
                        <div className="tw-flex tw-flex-wrap tw-gap-1 tw-min-w-0">
                          {vals.length > 0 ? (
                            vals.map((v, vi) => (
                              <span
                                key={`${attr.name}-v-${vi}`}
                                className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-bg-transparent tw-text-ink tw-text-xs tw-font-medium tw-border tw-border-edge tw-max-w-full tw-break-all"
                                title={v}
                              >
                                {v}
                              </span>
                            ))
                          ) : (
                            <span className="tw-text-sm tw-font-semibold tw-text-ink">-</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Search & Replace */}
          <div className="tw-w-[50%] tw-overflow-y-auto tw-p-4">
            {/* Section Header with conditional Replace button - per Figma 11671-11119 */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
              <Typography variant="title" className="tw-text-ink-secondary">
                {sectionTitle}
              </Typography>
              {selectedProductId && (
                <Button
                  variant="primary"
                  onClick={() => onReplaceProduct?.(selectedProductId)}
                  disabled={isReplacing}
                >
                  {actionButtonLabel}
                </Button>
              )}
            </div>

            {/* Search Input with icon */}
            <div className="tw-relative">
              <TextField
                label=""
                value={searchValue}
                onChange={setSearchValue}
                placeholder="Search"
                className="[&_input]:tw-pr-10"
              />
              <span className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-ink-secondary tw-pointer-events-none">
                <SearchIconSmall />
              </span>
            </div>

            {/* Search Results - per Figma 11915-16891 */}
            {searchValue && searchValue.length >= 2 && (
              <div className="tw-mt-4">
                {/* Loading State */}
                {isSearching && (
                  <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
                    <Typography variant="body-primary" className="tw-text-ink-secondary">
                      Searching...
                    </Typography>
                  </div>
                )}

                {/* Results */}
                {!isSearching && (
                  <>
                    {/* Results Badge */}
                    <div className="tw-mb-4">
                      <span className="tw-inline-flex tw-items-center tw-px-3 tw-py-0.5 tw-min-h-[28px] tw-bg-tone-neutral-tint tw-rounded-[13px] tw-text-sm tw-font-normal tw-text-ink">
                        Showing {searchResults.length} results
                      </span>
                    </div>

                    {/* No Results */}
                    {searchResults.length === 0 && (
                      <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
                        <Typography variant="body-primary" className="tw-text-ink-secondary">
                          No products found matching "{searchValue}"
                        </Typography>
                      </div>
                    )}

                    {/* Results Table */}
                    {searchResults.length > 0 && (
                      <div className="tw-border tw-border-edge tw-rounded">
                        {/* Table Header - per Figma */}
                        <div className="tw-flex tw-items-center tw-h-[44px] tw-bg-surface-sunken tw-border-b tw-border-edge">
                          <div className="tw-w-[36px] tw-flex-shrink-0" /> {/* Radio column */}
                          <div className="tw-flex-1 tw-min-w-0 tw-px-2">
                            <Typography variant="title-header" className="tw-text-ink-secondary">
                              Product Name
                            </Typography>
                          </div>
                          <div className="tw-w-[100px] tw-flex-shrink-0 tw-px-2">
                            <Typography variant="title-header" className="tw-text-ink-secondary">
                              Category
                            </Typography>
                          </div>
                          <div className="tw-w-[80px] tw-flex-shrink-0 tw-px-2">
                            <Typography variant="title-header" className="tw-text-ink-secondary">
                              Attributes
                            </Typography>
                          </div>
                          <div className="tw-w-[44px] tw-flex-shrink-0" /> {/* Expand column */}
                        </div>

                        {/* Table Rows */}
                        {searchResults.map((result, index) => (
                          <React.Fragment key={result.gms_id}>
                            {/* Main Row */}
                            <div
                              className={`tw-flex tw-items-center tw-h-[40px] tw-border-b tw-border-edge ${
                                index % 2 === 1 ? 'tw-bg-surface-sunken' : 'tw-bg-surface'
                              }`}
                            >
                              {/* Radio Button - per Figma (16px with grey-500 border) */}
                              <div className="tw-w-[36px] tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                                <button data-testid={`components-searchreplacesidepanel-button-2-${index}`}
                                  type="button"
                                  onClick={() => {
                                    setSelectedProductId(result.gms_id);
                                    onProductSelect?.(result.gms_id);
                                  }}
                                  className={`tw-w-4 tw-h-4 tw-rounded-full tw-border tw-flex tw-items-center tw-justify-center ${
                                    selectedProductId === result.gms_id
                                      ? 'tw-border-accent tw-bg-accent-bold'
                                      : 'tw-border-edge-strong tw-bg-surface'
                                  }`}
                                  aria-label={`Select ${result.name}`}
                                >
                                  {selectedProductId === result.gms_id && (
                                    <span className="tw-w-2 tw-h-2 tw-bg-ink-inverse tw-rounded-full" />
                                  )}
                                </button>
                              </div>
                              {/* Product Name — tw-min-w-0 on the flex child
                                  is required for the inner `tw-truncate` to
                                  actually shrink below the content width;
                                  without it, long names push the column out
                                  and overflow the whole row. Tooltip lives on
                                  the wrapper div since Typography doesn't
                                  accept a `title` prop. */}
                              <div className="tw-flex-1 tw-min-w-0 tw-px-2 tw-truncate" title={result.name}>
                                <Typography variant="title-header" className="tw-text-ink tw-truncate tw-block">
                                  {result.name}
                                </Typography>
                              </div>
                              {/* Category */}
                              <div className="tw-w-[100px] tw-flex-shrink-0 tw-min-w-0 tw-px-2 tw-truncate" title={result.category || '-'}>
                                <Typography variant="title-header" className="tw-text-ink tw-truncate tw-block">
                                  {result.category || '-'}
                                </Typography>
                              </div>
                              {/* Attributes Count */}
                              <div className="tw-w-[80px] tw-flex-shrink-0 tw-px-2">
                                <Typography variant="title-header" className="tw-text-ink tw-truncate">
                                  {result.attributes?.length || 0}
                                </Typography>
                              </div>
                              {/* Expand Icon - clickable toggle per Figma 11915-17125 */}
                              <div className="tw-w-[44px] tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                                <button data-testid={`components-searchreplacesidepanel-button-3-${index}`}
                                  type="button"
                                  onClick={() => handleRowExpand(result.gms_id)}
                                  className="tw-p-1 tw-rounded hover:tw-bg-surface-hover-strong tw-text-ink-secondary tw-transition-colors"
                                  aria-label={expandedRowId === result.gms_id ? 'Collapse row' : 'Expand row'}
                                >
                                  {expandedRowId === result.gms_id ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                </button>
                              </div>
                            </div>

                            {/* Expanded Content - per Figma 11915-17125 */}
                            {expandedRowId === result.gms_id && (
                              <div className="tw-border-b tw-border-edge tw-p-3">
                                {/* Attributes Table */}
                                {result.attributes && result.attributes.length > 0 && (
                                  <div className="tw-flex tw-flex-col tw-mb-3">
                                    {/* Attribute Header Row */}
                                    <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-2">
                                      <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">Attribute</span>
                                      <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">Attribute Value</span>
                                    </div>
                                    {/* Attribute Rows with striped backgrounds */}
                                    <div className="tw-flex tw-flex-col">
                                      {result.attributes.map((attr, attrIndex) => (
                                        <div
                                          key={attr.name}
                                          className={`tw-grid tw-grid-cols-2 tw-gap-4 tw-py-1.5 tw-px-2 ${
                                            attrIndex % 2 === 0 ? 'tw-bg-surface-sunken' : ''
                                          }`}
                                        >
                                          <span className="tw-text-sm tw-font-semibold tw-text-ink tw-min-w-0 tw-break-words">
                                            {attr.name}
                                          </span>
                                          {/* Render each value as an individual chip instead
                                              of a comma-joined string. */}
                                          <div className="tw-flex tw-flex-wrap tw-gap-1 tw-min-w-0">
                                            {attr.values && attr.values.length > 0 ? (
                                              attr.values.map((v, vi) => (
                                                <span
                                                  key={`${attr.name}-v-${vi}`}
                                                  className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-bg-transparent tw-text-ink tw-text-xs tw-font-medium tw-border tw-border-edge tw-max-w-full tw-break-all"
                                                  title={v}
                                                >
                                                  {v}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="tw-text-sm tw-font-semibold tw-text-ink">-</span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Metadata Grid */}
                                <div className="tw-grid tw-grid-cols-4 tw-gap-3 tw-pt-2 tw-border-t tw-border-edge">
                                  {renderMetadataItem('SKU', result.sku || '')}
                                  {renderMetadataItem('Price', result.price || '')}
                                  {renderMetadataItem('UPC', result.upc || '')}
                                  {renderMetadataItem('Model Number', result.model_number || '')}
                                  {renderMetadataItem('Real SKU', result.real_sku || '')}
                                  {renderMetadataItem('Parent ID', result.parent_id || '')}
                                  {renderMetadataItem('Version', result.version || '')}
                                  {renderMetadataItem('PLS', result.product_lifecycle_status || '')}
                                </div>
                                {result.description && (
                                  <div className="tw-mt-3 tw-pt-2 tw-border-t tw-border-edge">
                                    {renderMetadataItem('Description', result.description, true)}
                                  </div>
                                )}
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Empty State */}
            {!searchValue && (
              <div className="tw-mt-8 tw-text-center">
                <Typography variant="body-primary" className="tw-text-ink-secondary">
                  {emptyStateText}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchReplaceSidePanel;
